import {
  SalesPartnerGroup,
  useSalesPartnerPermissionMutation,
  useSalesPartnerPermissionUpdateMutation,
  useSnackbar,
} from '@e2m-customer-portal/hooks';
import { PermissionId } from '@e2m-customer-portal/shared';
import { ErrorComponent, PageTitle } from '@e2m-customer-portal/ui';
import { SalesPartner } from 'generated/graphql';
import { ReactElement } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';
import { isGranted as checkGranted } from '../../../utils';
import { PersonalSpaceSwitch } from '../personal-space-switch/PersonalSpaceSwitch';

export interface SalesPartnerSettingsProps {
  salesPartners: ReadonlyArray<SalesPartner | SalesPartnerGroup>;
}

export function SalesPartnerSettings({
  salesPartners,
}: Readonly<SalesPartnerSettingsProps>): ReactElement<SalesPartnerSettingsProps> {
  const [t] = useTranslation();
  const [updateLegacy] = useSalesPartnerPermissionMutation();
  const [update] = useSalesPartnerPermissionUpdateMutation();
  const { showError } = useSnackbar();

  const showSnackError = (): void => showError(t('SHARED_ERROR_UNKNOWN'));

  const isGranted = (salesPartner: SalesPartner | SalesPartnerGroup) => checkGranted(salesPartner.permissions);

  const toggleSeeForecast = (salesPartner: SalesPartner | SalesPartnerGroup): void => {
    const toggledState = !isGranted(salesPartner)(PermissionId.seeForecast);
    if ('contractPartnerNumber' in salesPartner)
      update(salesPartner.contractPartnerNumber, PermissionId.seeForecast, toggledState).catch(showSnackError);
    else updateLegacy(PermissionId.seeForecast, toggledState).catch(showSnackError);
    if (!toggledState) {
      if ('contractPartnerNumber' in salesPartner)
        update(salesPartner.contractPartnerNumber, PermissionId.editForecast, false).catch(showSnackError);
      else updateLegacy(PermissionId.editForecast, false).catch(showSnackError);
    }
  };

  const toggleEditForecast = (salesPartner: SalesPartner | SalesPartnerGroup): void => {
    const toggledState = !isGranted(salesPartner)(PermissionId.editForecast);
    if ('contractPartnerNumber' in salesPartner)
      update(salesPartner.contractPartnerNumber, PermissionId.editForecast, toggledState).catch(showSnackError);
    else updateLegacy(PermissionId.editForecast, toggledState).catch(showSnackError);
    if (toggledState) {
      if ('contractPartnerNumber' in salesPartner)
        update(salesPartner.contractPartnerNumber, PermissionId.seeForecast, true).catch(showSnackError);
      else updateLegacy(PermissionId.seeForecast, true).catch(showSnackError);
    }
  };

  const toggleSeeRevenue = (salesPartner: SalesPartner | SalesPartnerGroup): void => {
    if ('contractPartnerNumber' in salesPartner)
      update(
        salesPartner.contractPartnerNumber,
        PermissionId.seeRevenue,
        !isGranted(salesPartner)(PermissionId.seeRevenue),
      ).catch(showSnackError);
    else updateLegacy(PermissionId.seeRevenue, !isGranted(salesPartner)(PermissionId.seeRevenue)).catch(showSnackError);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <PageTitle title={t('SETTINGS_SALES_PARTNER_MY_SALES_PARTNERS')} />
      <div className="flex flex-wrap gap-6 h-full bg-grey-50 py-6 last-of-type:pb-0 lg:px-6">
        {salesPartners.map((salesPartner, index) => (
          <div key={salesPartner.name} className="w-full h-fit bg-white lg:w-fit lg:rounded-lg lg:shadow-sm">
            <div className="flex flex-col justify-between gap-2 bg-grey-100 px-6 py-4 lg:flex-row lg:rounded-t-lg">
              <div className="w-max">
                <div className="w-max text-sm text-grey-400">{t('SETTINGS_SALES_PARTNER')}</div>
                <div className="w-max font-semibold">{salesPartner.name}</div>
              </div>
              {'assignedMarketLocations' in salesPartner && (
                <div className="max-w-96 lg:text-end">
                  <div className="text-sm text-grey-400">{t('SETTINGS_SALES_PARTNER_MARKET_LOCATIONS')}</div>
                  {salesPartner.assignedMarketLocations
                    .map((maLo) =>
                      maLo.externalName?.length && maLo.externalName !== maLo.name
                        ? `${maLo.externalName} (${maLo.name})`
                        : maLo.name,
                    )
                    .join(', ')}
                </div>
              )}
            </div>
            <div className="mt-4 mx-6">
              <Trans components={{ b: <strong /> }} values={{ name: salesPartner.name }}>
                SETTINGS_SALES_PARTNER_CHANGE_CONFIG
              </Trans>
              <div className="mx-4 my-10 flex flex-col gap-4 lg:flex-row">
                <div>
                  <PersonalSpaceSwitch
                    checked={isGranted(salesPartner)(PermissionId.seeForecast)}
                    onChange={() => toggleSeeForecast(salesPartner)}
                    switchTitle={t('SETTINGS_SALES_PARTNER_SEE_FORECAST')}
                    switchInfoAllowed={t('SETTINGS_SALES_PARTNER_INFO_SEE_FORECAST_ALLOWED')}
                    switchInfoForbidden={t('SETTINGS_SALES_PARTNER_INFO_SEE_FORECAST_FORBIDDEN')}
                    dataTestId={`see-forecast-switch-${index}`}
                  />
                </div>
                <div className="mt-[40px] border-0 lg:mt-0 lg:border-l lg:border-solid lg:border-grey-300 lg:pb-1 lg:pl-4">
                  <PersonalSpaceSwitch
                    checked={isGranted(salesPartner)(PermissionId.editForecast)}
                    onChange={() => toggleEditForecast(salesPartner)}
                    switchTitle={t('SETTINGS_SALES_PARTNER_EDIT_FORECAST')}
                    switchInfoAllowed={t('SETTINGS_SALES_PARTNER_INFO_EDIT_FORECAST_ALLOWED')}
                    switchInfoForbidden={t('SETTINGS_SALES_PARTNER_INFO_EDIT_FORECAST_FORBIDDEN')}
                    dataTestId={`edit-forecast-switch-${index}`}
                  />
                </div>
              </div>
              <div className="mb-10 ml-4">
                <PersonalSpaceSwitch
                  checked={isGranted(salesPartner)(PermissionId.seeRevenue)}
                  onChange={() => toggleSeeRevenue(salesPartner)}
                  switchTitle={t('SETTINGS_SALES_PARTNER_SEE_REVENUE')}
                  switchInfoAllowed={t('SETTINGS_SALES_PARTNER_INFO_SEE_REVENUE_ALLOWED')}
                  switchInfoForbidden={t('SETTINGS_SALES_PARTNER_INFO_SEE_REVENUE_FORBIDDEN')}
                  dataTestId={`see-revenue-switch-${index}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </ErrorBoundary>
  );
}
